import React from "react";
import { Link } from "react-router-dom";
import { Heart, Sparkles, ArrowRight } from "lucide-react";

/**
 * DonateCTA – A visually striking, reusable call-to-action banner
 * that can be dropped into any page to encourage donations.
 *
 * Props:
 *  - variant: "warm" (default, orange/gold gradient) | "purple" (KU purple gradient)
 *  - headline: Override the default headline text
 *  - subtext: Override the default subtext
 */
const DonateCTA = ({
  variant = "warm",
  headline,
  subtext,
}) => {
  const isWarm = variant === "warm";

  const bgClass = isWarm
    ? "bg-gradient-to-br from-[#F5A623] via-[#F7812D] to-[#E8593C]"
    : "bg-gradient-to-br from-[#5D3294] via-[#7B4EAE] to-[#3B1E60]";

  const defaultHeadline = "Your Kindness Can Change Lives";
  const defaultSubtext =
    "Every contribution — big or small — fuels our mission to spread kindness across India. Donate today and create ripples of change.";

  return (
    <section className={`relative w-full overflow-hidden ${bgClass}`}>
      {/* Shimmer overlay */}
      <div className="donate-cta-shimmer absolute inset-0 pointer-events-none" />

      {/* Decorative circles */}
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/5 pointer-events-none" />
      <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-white/5 pointer-events-none" />
      <div className="absolute top-1/2 right-1/4 w-32 h-32 rounded-full bg-white/[0.03] pointer-events-none" />

      <div className="relative z-10 ku-container py-16 md:py-20 lg:py-24">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          {/* Eyebrow badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 border border-white/25 text-xs font-bold uppercase tracking-widest text-white/90 backdrop-blur-sm">
            <Sparkles className="w-4 h-4" />
            Make a Difference
          </div>

          {/* Headline */}
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight tracking-tight">
            {headline || defaultHeadline}
          </h2>

          {/* Subtext */}
          <p className="text-white/85 text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl mx-auto font-medium">
            {subtext || defaultSubtext}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Link
              to="/donate"
              className="donate-cta-btn inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full font-accent font-bold text-base text-[#3B1E60] bg-white shadow-xl border-none cursor-pointer"
            >
              <Heart className="w-5 h-5 text-red-500 donate-heart-beat" fill="currentColor" />
              Donate Now
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DonateCTA;
